import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import { useTranslation } from '../../utils/translations';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import supabase from '../../lib/supabase';
import { canAccessPage } from '../../utils/permissions';

const {
  FiMessageSquare, FiBarChart2, FiUser, FiSettings, FiUsers, FiHelpCircle, FiMoon, FiSun,
  FiHeadphones, FiChevronLeft, FiChevronRight, FiLogOut, FiLifeBuoy, FiCode, FiClock
} = FiIcons;
const { FaRobot } = FaIcons;

// Exportable menu items configuration
export const menuItemsConfig = (t, user, key = "") => {
  // Base items (no role checks here)
  const baseItems = [
    {
      id: 'agents',
      icon: FiMessageSquare,
      labelKey: 'chatAgents',
      label: t("sideBar.chatAgents", "Chat"),
      title: t("sideBar.chatAgentsTitle", "AI Chat Interface"),
      subtitle: t("sideBar.chatAgentsSubtitle", "Interact with AI agents"),
      render: true,
    },
    {
      id: 'statistics',
      icon: FiBarChart2,
      labelKey: 'dashboard',
      label: t("sideBar.dashboard", "Dashboard"),
      title: t("sideBar.dashboardTitle", "Analytics Dashboard"),
      subtitle: t("sideBar.dashboardSubtitle", "View performance metrics"),
      render: true,
    },
    {
      id: 'ai-agents',
      icon: FaRobot,
      labelKey: 'aiAgents',
      label: t("sideBar.aiAgents", "AI Agents"),
      title: t("sideBar.aiAgentsTitle", "AI Agents Management"),
      subtitle: t("sideBar.aiAgentsSubtitle", "Manage AI agent configurations"),
      render: true,
    },
    {
      id: 'profile',
      icon: FiUser,
      labelKey: 'profile',
      label: t("sideBar.profile", "Profile"),
      title: t("sideBar.profileTitle", "User Profile"),
      subtitle: t("sideBar.profileSubtitle", "Manage your account"),
      render: false,
    },
    {
      id: 'widgets',
      icon: FiCode,
      labelKey: 'widgets',
      label: t("sideBar.widgets", "Widgets"),
      title: t("sideBar.widgetsTitle", "Custom Widgets"),
      subtitle: t("sideBar.widgetsSubtitle", "Create and manage widgets"),
      render: true,
    },
    {
      id: 'settings',
      icon: FiSettings,
      labelKey: 'settings',
      label: t("sideBar.settings", "Settings"),
      title: t("sideBar.settingsTitle", "Application Settings"),
      subtitle: t("sideBar.settingsSubtitle", "Configure your preferences"),
      render: true,
    },
    {
      id: 'team-management',
      icon: FiUsers,
      labelKey: 'teamManagement',
      label: t("sideBar.teamManagement", "Team Management"),
      title: t("sideBar.teamManagementTitle", "Team Management"),
      subtitle: t("sideBar.teamManagementSubtitle", "Administer team settings"),
      render: true,
    },
    {
      id: 'help-center',
      icon: FiLifeBuoy,
      labelKey: 'helpCenter',
      label: t("sideBar.helpCenter", "Help Center"),
      title: t("sideBar.helpCenterTitle", "Help Center"),
      subtitle: t("sideBar.helpCenterSubtitle", "Get support and resources"),
      render: true,
    },
  ];

  // Final render gating via canAccessPage
  const gated = baseItems.map(item => ({
    ...item,
    render: item.render !== false && canAccessPage(user, item.id),
  }));

  return key ? gated.find(item => item.id === key) : gated;
};

const Sidebar = ({ activeTab, setActiveTab }) => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const t = useTranslation(user?.language || 'fr');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [appLogo, setAppLogo] = useState('https://ui-avatars.com/api/?name=AI&background=007ee5&color=fff&size=200&bold=true');

  // Load sidebar state from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('sidebar_collapsed');
    if (savedState !== null) {
      setIsCollapsed(savedState === 'true');
    }
  }, []);

  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', isCollapsed.toString());
  }, [isCollapsed]);

  // Fetch app logo from settings + realtime updates
  useEffect(() => {
    const fetchAppLogo = async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings_a1b2c3d4e5')
          .select('value')
          .eq('key', 'app_logo')
          .single();
        if (error) throw error;
        if (data && data.value) setAppLogo(data.value);
      } catch (error) {
        console.error('Error fetching app logo:', error);
      }
    };
    fetchAppLogo();

    const channel = supabase
      .channel('app_settings_changes')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'app_settings_a1b2c3d4e5',
        filter: 'key=eq.app_logo'
      }, (payload) => {
        if (payload.new?.value) setAppLogo(payload.new.value);
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  const menuItems = menuItemsConfig(t, user).filter(item => item.render);

  const toggleSidebar = () => setIsCollapsed(!isCollapsed);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleSupportClick = () => {
    window.open('https://prosomo.com', '_blank');
  };

  return (
    <div className={`
      bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 
      flex flex-col h-full transition-all duration-300 relative
      ${isCollapsed ? 'w-20' : 'w-64'}
      hidden lg:flex
    `}>
      {/* Toggle Button */}
      <div className="absolute -right-3 top-6 z-20">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={toggleSidebar}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full p-1.5 shadow-md hover:shadow-lg transition-shadow"
        >
          <SafeIcon icon={isCollapsed ? FiChevronRight : FiChevronLeft} className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </motion.button>
      </div>

      {/* Logo Section */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-center">
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'}`}>
          <div className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center">
            <img
              src={appLogo}
              alt={t('sideBar.appLogoAlt', 'Prosomo')}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = "https://ui-avatars.com/api/?name=AI&background=007ee5&color=fff&size=200&bold=true";
              }}
            />
          </div>
          {!isCollapsed && (
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                {t('sideBar.appName', 'Prosomo')}
              </h1>
            </div>
          )}
        </div>
      </div>

      {/* User Role Debug Info - Only show in development */}
      {process.env.NODE_ENV === 'development' && !isCollapsed && user && (
        <div className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-xs">
          <div className="text-gray-600 dark:text-gray-400">
            {t('sideBar.roleLabel', 'Role:')} <span className="font-mono text-blue-600 dark:text-blue-400">{user.role}</span>
          </div>
          <div className="text-gray-600 dark:text-gray-400">
            {t('sideBar.idLabel', 'ID:')} <span className="font-mono text-gray-500">{user.id?.slice(0)}...</span>
          </div>
        </div>
      )}

      {/* Navigation menu */}
      <nav className="flex-1 px-4 py-6 overflow-y-auto">
        <ul className="space-y-2">
          {menuItems.map((item) => (
            <li key={item.id}>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-start'} px-4 py-3 rounded-xl text-left transition-all ${activeTab === item.id ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'}`}
                title={isCollapsed ? item.label : ''}
              >
                <SafeIcon
                  icon={item.icon}
                  className={`w-5 h-5 ${activeTab === item.id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}
                />
                {!isCollapsed && (
                  <span className="ml-3 font-medium">
                    {item.label}
                  </span>
                )}
              </motion.button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom Section */}
      <div className="px-4 py-6 border-t border-gray-200 dark:border-gray-700">
        <div className="space-y-3">
          {!isCollapsed && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiHeadphones} className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    {t("sideBar.technicalSupport", "Technical Support")}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t("sideBar.needHelp", "Need help?")}
                  </p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSupportClick}
                className="w-full bg-blue-500 text-white text-sm font-medium py-2 px-3 rounded-lg hover:bg-blue-600 transition-colors"
              >
                {t("sideBar.contactSupport", "Contact Support")}
              </motion.button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;